import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export type DurationKey = 'any' | 'lt30' | '30to60' | 'gt60';

interface EventsFilterValue {
  /** Type filter keys: 'livestream' | 'liveroom' | 'offline' | 'online'. */
  types: string[];
  /** Selected direction names (canonical RU labels). */
  paths: string[];
  duration: DurationKey;
  toggleType: (key: string) => void;
  togglePath: (name: string) => void;
  setTypes: (keys: string[]) => void;
  setPaths: (names: string[]) => void;
  setDuration: (d: DurationKey) => void;
  reset: () => void;
  /** True when any filter is narrowing the list. */
  hasActive: boolean;
}

const EventsFilterContext = createContext<EventsFilterValue | null>(null);

/**
 * Shared events filter state (type / direction / duration) for the native
 * «События» flow. Lives above the navigator so the hub, «Все события»,
 * «Календарь» and the «Фильтры» screen all read and write one source of truth
 * — picking a filter on the Фильтры screen reflects everywhere instantly.
 *
 * useLivesCalendar fetches filter-independently (all events, filtered client-
 * side), so the filters live here rather than inside that hook.
 */
export const EventsFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [types, setTypes] = useState<string[]>([]);
  const [paths, setPaths] = useState<string[]>([]);
  const [duration, setDuration] = useState<DurationKey>('any');

  const toggleType = useCallback((key: string) => {
    setTypes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  const togglePath = useCallback((name: string) => {
    setPaths((prev) => (prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name]));
  }, []);

  const reset = useCallback(() => {
    setTypes([]);
    setPaths([]);
    setDuration('any');
  }, []);

  const value = useMemo<EventsFilterValue>(
    () => ({
      types,
      paths,
      duration,
      toggleType,
      togglePath,
      setTypes,
      setPaths,
      setDuration,
      reset,
      hasActive: types.length > 0 || paths.length > 0 || duration !== 'any',
    }),
    [types, paths, duration, toggleType, togglePath, reset],
  );

  return <EventsFilterContext.Provider value={value}>{children}</EventsFilterContext.Provider>;
};

export function useEventsFilter(): EventsFilterValue {
  const ctx = useContext(EventsFilterContext);
  if (!ctx) throw new Error('useEventsFilter must be used within EventsFilterProvider');
  return ctx;
}

/** Whether an event's duration (minutes) passes the duration filter. */
export const durationMatches = (mins: number | null | undefined, key: DurationKey): boolean => {
  if (key === 'any' || mins == null) return true;
  if (key === 'lt30') return mins < 30;
  if (key === '30to60') return mins >= 30 && mins <= 60;
  return mins > 60;
};
